// import * as Sentry from "@sentry/nextjs"
import { createContext, type FC, type ReactNode, useCallback, useEffect, useState } from "react"

import type { Cart } from "../components/Cart"
import { PPModal } from "../components/PPModal"
import { ToSModal } from "../components/ToSModal"
import {
  type BaseProductFragment,
  CheckoutMode,
  type DeliveryMethod,
  type PurchasePaymentMethod,
  UserActivityEventType,
  useCalculateDiscountAndFeesMutation,
} from "../generated/graphql"
import { graphQLClient } from "../graphql/client"
import { productInCartCount } from "../utils/cart"
import { formatVariantKey } from "../utils/checkout"
import { processException } from "../utils/errors"
import { useTrackUserActivity } from "../utils/tracking"

interface AppProviderProps {
  children: ReactNode | ReactNode[]
}

type AdjustCartFunc = (
  code: string,
  product: BaseProductFragment,
  quantity: number,
  variant?: { size: string; color: string; printfulVariantId?: number },
  deliveryMethod?: DeliveryMethod,
  fairPrice?: number,
) => void

interface IContextProps {
  showToSModal: () => void
  hideToSModal: () => void
  showPPModal: () => void
  hidePPModal: () => void
  cart: Cart
  adjustCart: AdjustCartFunc
  requestedPurchases: string[]
  setRequestedPurchases: (purchases: string[]) => void
  clearCart: (code?: string) => void
  setPromocodeId: (id: string) => void
  promocodeId: string
  additionalModalVisible: boolean
  setAdditionalModalVisible: (value: boolean) => void
  commentModalVisible: boolean
  setCommentModalVisible: (value: boolean) => void
  // Cart open state
  isCartOpen: boolean
  openCart: () => void
  closeCart: () => void
  // helper functions
  subtotal: (code: string) => number
  calculateDiscountAndFees: (
    code: string,
    paymentMethod: PurchasePaymentMethod,
    promocodeId: string,
    shippingCountry?: string,
    printfulShippingFee?: number,
  ) => Promise<
    | {
        discount?: number | null
        fees?: number | null
        deliveryFees?: number | null
        vat?: number | null
        invalidProductIds?: string[] | null
        checkoutMode?: CheckoutMode | null
        paymentMethods?: PurchasePaymentMethod[] | null
      }
    | null
    | undefined
  >
}

export const AppContext = createContext({} as IContextProps)

const AppProvider: FC<AppProviderProps> = ({ children }) => {
  const { mutateAsync: calcFees } = useCalculateDiscountAndFeesMutation(graphQLClient)
  const track = useTrackUserActivity()

  const [showToS, setShowToS] = useState(false)
  const [showPP, setShowPP] = useState(false)

  const [promocodeId, setPromocodeId] = useState("")

  const [additionalModalVisible, setAdditionalModalVisible] = useState(false)
  const [commentModalVisible, setCommentModalVisible] = useState(false)

  const showToSModal = () => setShowToS(true)
  const hideToSModal = () => setShowToS(false)
  const showPPModal = () => setShowPP(true)
  const hidePPModal = () => setShowPP(false)

  // Cart open state for controlled cart dropdown
  const [isCartOpen, setIsCartOpen] = useState(false)
  const openCart = useCallback(() => setIsCartOpen(true), [])
  const closeCart = useCallback(() => setIsCartOpen(false), [])

  const [cart, _setCart] = useState<Cart>({})

  const [requestedPurchases, _setRequestedPurchases] = useState<string[]>([])

  useEffect(() => {
    try {
      const persistedCart = JSON.parse(window.localStorage.getItem("cart") ?? "{}") as Cart
      const persistedRequestedPurchases = JSON.parse(
        window.localStorage.getItem("requestedPurchases") ?? "[]",
      ) as string[]

      _setCart(persistedCart)
      _setRequestedPurchases(persistedRequestedPurchases)
    } catch {
      _setCart({})
      _setRequestedPurchases([])
    }
  }, [])

  const setRequestedPurchases = useCallback((purchases: string[]) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("requestedPurchases", JSON.stringify(purchases))
    }
    _setRequestedPurchases(purchases)
  }, [])

  const setCart = useCallback(
    (c: Cart) => {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("cart", JSON.stringify(c))
      }
      _setCart(c)
      // if cart empty reset requestedPurchases
      if (Object.keys(c).length === 0) setRequestedPurchases([])
    },
    [setRequestedPurchases],
  )

  const subtotal = useCallback(
    (code: string) => {
      return cart[code]
        ? Object.values(cart[code]).reduce(
            (acc, cv) => (cv.fairPrice ?? cv.product.price) * cv.quantity + acc,
            0,
          )
        : 0
    },
    [cart],
  )

  const calculateDiscountAndFees = useCallback(
    async (
      code: string,
      paymentMethod: PurchasePaymentMethod,
      promocodeId: string,
      shippingCountry?: string,
      printfulShippingFee?: number,
    ) => {
      try {
        // An emptied cart stays as {}: no fees to ask for, and checkout navigating back would cut
        // the request off as "Load failed".
        if (cart[code] && productInCartCount(cart[code]) > 0) {
          const r = await calcFees({
            cart: Object.keys(cart[code]).map((key) => {
              const cartItem = cart[code][key]
              return {
                deliveryMethod: cartItem.deliveryMethod,
                productId: cartItem.product.id,
                quantity: cartItem.quantity,
                fairPrice: cartItem.fairPrice,
                variant: cartItem.variant,
              }
            }),
            paymentMethod: paymentMethod,
            promocodeId,
            printfulShippingFee,
            shippingCountry,
          })

          // Handle invalid products by removing them from cart
          const result = r.calculateDiscountAndFees
          if (result?.invalidProductIds && result.invalidProductIds.length > 0) {
            const newCart = { ...cart }
            result.invalidProductIds.forEach((invalidProductId) => {
              if (newCart[code]?.[invalidProductId]) {
                delete newCart[code][invalidProductId]
              }
            })
            setCart(newCart)
          }

          return result
        }
        return {
          checkoutMode: CheckoutMode.Purchase,
          discount: 0,
          fees: 0,
          invalidProductIds: [],
          paymentMethods: [],
          vat: 0,
        }
      } catch (err) {
        processException(err)
        throw err
      }
    },
    [cart, calcFees, setCart],
  )

  const adjustCart: AdjustCartFunc = useCallback(
    (code, product, quantity, variant, deliveryMethod, fairPrice) => {
      const newCart = { ...cart }
      if (!Object.keys(newCart).includes(code)) {
        newCart[code] = {}
      } else {
        // Create new reference for the community cart so cartForCommunity detects changes
        newCart[code] = { ...newCart[code] }
      }

      // Create unique key for product + variant combination
      const cartKey = variant ? `${product.id}-${formatVariantKey(variant)}` : product.id

      if (!Object.keys(newCart[code]).includes(cartKey)) {
        newCart[code][cartKey] = {
          deliveryMethod,
          product,
          quantity: 0,
          fairPrice,
          variant,
        }
      }
      newCart[code][cartKey].product = product
      newCart[code][cartKey].quantity += quantity
      if (fairPrice !== undefined) {
        newCart[code][cartKey].fairPrice = fairPrice
      }
      // Update delivery method if provided
      if (deliveryMethod !== undefined) {
        newCart[code][cartKey].deliveryMethod = deliveryMethod
      }
      if (newCart[code][cartKey].quantity === 0) {
        delete newCart[code][cartKey]
      }
      // A zero change only updates the line (checkout syncs delivery methods this way); it is not
      // a cart add.
      if (quantity !== 0) {
        track({
          data: {
            communityCode: code,
            eventId: product.event?.id,
            productId: product.id,
            variant: variant ? formatVariantKey(variant) : undefined,
          },
          type:
            quantity < 0
              ? UserActivityEventType.ProductCardRemove
              : UserActivityEventType.ProductCardAdd,
        })
      }
      setCart(newCart)
      // if cart empty reset requestedPurchases
      // do this by calculating total quantities
      const totalQuantity = Object.values(newCart[code]).reduce((acc, cv) => cv.quantity + acc, 0)
      if (totalQuantity === 0) setRequestedPurchases([])
    },
    [cart, track, setCart, setRequestedPurchases],
  )

  const clearCart = useCallback(
    (code?: string) => {
      const newCart = { ...cart }
      if (!code) {
        setCart({})
        return
      }
      newCart[code] = {}
      setCart(newCart)
    },
    [cart, setCart],
  )

  const contextProps = {
    additionalModalVisible,
    adjustCart,
    calculateDiscountAndFees,
    cart,
    clearCart,
    closeCart,
    commentModalVisible,
    hidePPModal,
    hideToSModal,
    isCartOpen,
    openCart,
    promocodeId,
    requestedPurchases,
    setAdditionalModalVisible,
    setCommentModalVisible,
    setPromocodeId,
    setRequestedPurchases,
    showPPModal,
    showToSModal,
    subtotal,
  }

  return (
    <AppContext.Provider value={contextProps}>
      {children}
      <ToSModal isOpen={showToS} />
      <PPModal isOpen={showPP} />
    </AppContext.Provider>
  )
}

export default AppProvider
